import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule, NgIf, NgFor, NgSwitch, NgSwitchCase } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';

import { NotificationService } from './../../../../Services/notification.service';
import { OrderCreationService } from '../../../../Services/order-creation.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';

import { AllOrdersComponent } from '../all-orders/all-orders.component';
import { DeliveredOrdersComponent } from "../delivered-orders/delivered-orders.component";
import { RejectedOrdersComponent } from "../rejected-orders/rejected-orders.component";
import { PendingOrdersComponent } from "../pending-orders/pending-orders.component";
import { AuthService } from '../../../../Services/auth.service';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-order',
  standalone: true,
  imports: [
    CommonModule,
    NgSwitch,
    NgSwitchCase,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    AllOrdersComponent,
    DeliveredOrdersComponent,
    RejectedOrdersComponent,
    PendingOrdersComponent
  ],
  providers: [DatePipe],
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.css']
})
export class OrderComponent implements OnInit, OnDestroy {
  isLoading: boolean = true;
  allOrders: any[] = [];
  deliveredOrders: any[] = [];
  pendingOrders: any[] = [];
  rejectedOrders: any[] = [];

  selectedStatus: string = 'all';

  // Orders Pagination State
  ordersCurrentPage: number = 1;
  ordersPageSize: number = 10;
  ordersTotal: number = 0;
  paginatedOrders: any[] = [];

  // History Modal State
  showHistoryModal = false;
  transactions: any[] = [];
  isLoadingHistory = false;
  fromDate: string = '';
  toDate: string = '';
  currentPage: number = 1;
  pageSize: number = 20;
  totalTransactions: number = 0;
  lastPage: number = 1;
  public Math = Math;

  private subscriptions = new Subscription();

  constructor(
    private orderservice: OrderCreationService,
    private notificationService: NotificationService,
    private firmSelectionService: FirmSelectionService, // <-- 1. Inject the service
    private authService: AuthService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    // const supplierId = localStorage.getItem('supplierId');
    const supplierId = this.authService.getUserId();
    if (!supplierId) {
      this.notificationService.showError("Supplier ID not found", "Error");
      this.isLoading = false;
      return;
    }

    // 2. Subscribe to firm changes
    this.subscriptions.add(
      this.firmSelectionService.selectedFirmName$.subscribe(firm => {
        if (firm) {
          this.getOrderList(supplierId, firm.id.toString());
        }
      })
    );

    // Set default dates for history
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    this.fromDate = this.formatDate(firstDay);
    this.toDate = this.formatDate(now);

    // 3. Handle initial load
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.getOrderList(supplierId, currentFirm.id.toString());
    }
  }

  private formatDate(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  toggleHistoryModal() {
    this.showHistoryModal = !this.showHistoryModal;
    if (this.showHistoryModal) {
      this.getKalakarTransactions();
    }
  }

  getKalakarTransactions(page: number = 1) {
    this.isLoadingHistory = true;
    this.currentPage = page;

    const supplierId = this.authService.getUserId();

    if (!supplierId) {
      this.isLoadingHistory = false;
      return;
    }

    let params = new HttpParams()
      .set('kalakar_id', supplierId.toString())
      .set('page', page.toString())
      .set('per_page', this.pageSize.toString());

    if (this.fromDate) params = params.set('from_date', this.fromDate);
    if (this.toDate) params = params.set('to_date', this.toDate);

    this.orderservice.getKalakarTransactions(params).subscribe({
      next: (res: any) => {
        this.transactions = res?.data?.data || [];
        this.totalTransactions = res?.data?.total || 0;
        this.lastPage = res?.data?.last_page || 1;
        this.isLoadingHistory = false;
      },
      error: (err: any) => {
        console.error('Error fetching transactions:', err);
        this.isLoadingHistory = false;
      }
    });
  }

  onHistoryPageSizeChange() {
    this.currentPage = 1;
    this.getKalakarTransactions();
  }

  clearTransactionsFilter() {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    this.fromDate = this.formatDate(firstDay);
    this.toDate = this.formatDate(now);
    this.currentPage = 1;
    this.getKalakarTransactions();
  }

  formatTransactionType(type: string): string {
    if (!type) return '';
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  downloadHistoryPDF() {
    const doc = new jsPDF('l', 'mm', 'a4');
    const headers = [['Date', 'Type', 'Code', 'Product', 'Qty', 'Amount', 'Remarks']];
    const data = this.transactions.map(tx => [
      this.datePipe.transform(tx.created_at, 'dd-MMM-yyyy hh:mm a'),
      this.formatTransactionType(tx.ot_type),
      tx.ot_product_code,
      tx.ot_product_name,
      tx.ot_quantity,
      tx.ot_amount > 0 ? `Rs. ${tx.ot_amount}` : '-',
      tx.ot_remarks || '-'
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] } // indigo-600
    });
    doc.save(`Transaction_History_${new Date().getTime()}.pdf`);
  }

  printHistory() {
    let printContent = `
      <html>
        <head>
          <title>Transaction History</title>
          <style>
            table { width: 100%; border-collapse: collapse; font-family: sans-serif; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #f3f4f6; font-weight: bold; }
            h2 { text-align: center; color: #4f46e5; }
          </style>
        </head>
        <body>
          <h2>Transaction History</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Code</th>
                <th>Product</th>
                <th>Qty</th>
                <th>Amount</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              ${this.transactions.map(tx => `
                <tr>
                  <td>${this.datePipe.transform(tx.created_at, 'dd-MMM-yyyy hh:mm a')}</td>
                  <td>${this.formatTransactionType(tx.ot_type)}</td>
                  <td>${tx.ot_product_code}</td>
                  <td>${tx.ot_product_name}</td>
                  <td>${tx.ot_quantity}</td>
                  <td>${tx.ot_amount > 0 ? 'Rs. ' + tx.ot_amount : '-'}</td>
                  <td>${tx.ot_remarks || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  }

  downloadOrdersPDF() {
    const doc = new jsPDF('l', 'mm', 'a4');
    const headers = [['Code', 'Product', 'Assigned Qty', 'Available Qty', 'Ready Qty', 'Approved Qty', 'Rejected Qty', 'Status']];
    const data = (this.selectedStatus === 'all' ? this.allOrders :
      this.selectedStatus === 'pending' ? this.pendingOrders :
        this.selectedStatus === 'delivered' ? this.deliveredOrders : this.rejectedOrders)
      .map(o => [
        o.product_code,
        o.product_name,
        o.quantity,
        o.ordc_available_quantity,
        o.ready_quantity,
        o.approved_quantity,
        o.rejected_quantity,
        o.status
      ]);

    autoTable(doc, {
      head: headers,
      body: data,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] }
    });
    doc.save(`Order_List_${this.selectedStatus}_${new Date().getTime()}.pdf`);
  }

  printOrders() {
    const list = this.selectedStatus === 'all' ? this.allOrders :
      this.selectedStatus === 'pending' ? this.pendingOrders :
        this.selectedStatus === 'delivered' ? this.deliveredOrders : this.rejectedOrders;

    let printContent = `
      <html>
        <head>
          <title>Order List - ${this.selectedStatus.toUpperCase()}</title>
          <style>
            table { width: 100%; border-collapse: collapse; font-family: sans-serif; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f3f4f6; }
            h2 { text-align: center; }
          </style>
        </head>
        <body>
          <h2>Order List (${this.selectedStatus.toUpperCase()})</h2>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Product</th>
                <th>Assigned</th>
                <th>Available</th>
                <th>Ready</th>
                <th>Approved</th>
                <th>Rejected</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${list.map(o => `
                <tr>
                  <td>${o.product_code}</td>
                  <td>${o.product_name}</td>
                  <td>${o.quantity}</td>
                  <td>${o.ordc_available_quantity}</td>
                  <td>${o.ready_quantity}</td>
                  <td>${o.approved_quantity}</td>
                  <td>${o.rejected_quantity}</td>
                  <td>${o.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
  }

  // 4. Update getOrderList to accept firmId
  getOrderList(supplierId: string, firmId: string): void {
    this.isLoading = true;

    // Add firm_id to the request parameters
    let params = new HttpParams()
      .set("supplier_id", supplierId);

    this.orderservice.getOrders(params).subscribe({
      next: (res: any) => {
        const rawOrders = res?.data?.data || res?.data || [];
        console.log("Orders data is ", rawOrders);
        const orders = Array.isArray(rawOrders) ? rawOrders
          //  1. FILTER by handover_status
          .filter((order: any) => order.handover_status === 'handed_over')
          //  2. MAP status safely
          .map((order: any) => ({
            ...order,
            status:
              order.status === 'current' || !order.status
                ? 'pending'
                : order.status
          })) : [];
          console.log("final Orders data is ", orders);
        this.filterOrdersByStatus(orders);
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error("Error fetching orders:", err);
        this.notificationService.showError("Failed to fetch orders", "Error");
        this.isLoading = false;
      }
    });
  }

  filterOrdersByStatus(allOrders: any[]): void {
    this.allOrders = [...allOrders];
    this.pendingOrders = allOrders.filter(order => order.status === 'pending');
    this.deliveredOrders = allOrders.filter(order => order.status === 'completed');
    this.rejectedOrders = allOrders.filter(order => order.status === 'rejected');
    this.updatePaginatedOrders();
  }

  updatePaginatedOrders() {
    const currentList = this.selectedStatus === 'all' ? this.allOrders :
      this.selectedStatus === 'pending' ? this.pendingOrders :
        this.selectedStatus === 'delivered' ? this.deliveredOrders : this.rejectedOrders;

    this.ordersTotal = currentList.length;
    const start = (this.ordersCurrentPage - 1) * this.ordersPageSize;
    this.paginatedOrders = currentList.slice(start, start + this.ordersPageSize);
  }

  onOrdersPageChange(page: number) {
    this.ordersCurrentPage = page;
    this.updatePaginatedOrders();
  }

  onOrdersPageSizeChange() {
    this.ordersCurrentPage = 1;
    this.updatePaginatedOrders();
  }

  onStatusChange() {
    this.ordersCurrentPage = 1;
    this.updatePaginatedOrders();
  }

  onOrderUpdated(event: { orderId: number, ready_quantity: number, ordc_request_status: string }): void {
    console.log("Order updated event received:", event);
    this.isLoading = true;

    this.orderservice.updateOrder(event.orderId, {
      ordc_ready_quantity: event.ready_quantity,
      ordc_request_status: event.ordc_request_status,
      ordc_rejected_quantity: 0
    }).subscribe({
      next: (res) => {
        this.notificationService.showSuccess("Order updated successfully", "Success");
        // Re-fetch the data for the currently selected firm after an update
        const supplierId = localStorage.getItem('supplierId');
        const currentFirm = this.firmSelectionService.getselectedFirmName();
        if (supplierId && currentFirm) {
          this.getOrderList(supplierId, currentFirm.id.toString());
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.notificationService.showError("Failed to update order", "Error");
        console.error("Error updating order:", err);
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
